import { CancelOutlined as Remove } from "@mui/icons-material";
import {
  Button,
  IconButton,
  MenuItem,
  Select,
  Stack,
  SxProps,
  TextField,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import {
  handleAddRemoveLogicJumps,
  handleLogicJumpsChange,
} from "../../../Redux/reducers/questionSlice";
import { conditionOptionMap } from "./ValidationTab";
import { requiredStyles } from "./BasicTab";
import LogicJumpDatePickers from "./LogicJumpDatePickers";

type Props = {
  question: any;
};

const fieldArray: SxProps = {
  mb: 3,
  border: "1px solid",
  borderColor: "#E5E7EB",
};

const fieldHeader: SxProps = {
  px: 2,
  py: 1,
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  borderBottom: "1px solid",
  borderColor: "#E5E7EB",
};

const fieldContent: SxProps = {
  px: 2,
  py: 1,
  gap: 4,
};

const LogicJumpsTab = ({ question }: Props) => {
  const { logicJumps }: { logicJumps: any[] } = question || {};
  const { choices }: { choices: any[] } = question || {};

  const dispatch = useAppDispatch();
  const isLogicJumpsError = useAppSelector(
    ({ question }) => question.isLogicJumpsError
  );
  const isPreview = useAppSelector(({ question }) => !question.editable);
  const questions = useAppSelector(({ question }) => question.questions);

  // Filter questions to exclude current question and no-value types
  const availableQuestions =
    questions
      ?.filter(
        (q: any) => q.id !== question?.id && q.position > question.position
      )
      .sort(
        (a: { position: number }, b: { position: number }) =>
          a.position - b.position
      ) || [];

  const onChange = (
    key: string,
    index: number,
    event: any,
    convertToNum?: boolean
  ) => {
    dispatch(
      handleLogicJumpsChange({
        key,
        value: event.target.value,
        index,
        convertToNum,
      })
    );
  };

  let qType = question.type;

  if (
    qType === "calculated_field" &&
    question.properties?.responseType === "string"
  ) {
    qType = "calculated_field_text";
  }

  return (
    <Stack gap={2} py={1}>
      {isLogicJumpsError && (
        <Typography variant="caption" fontWeight="medium" color={"red"}>
          {isLogicJumpsError}
        </Typography>
      )}

      {logicJumps &&
        logicJumps.map((jump, i) => (
          <Stack sx={fieldArray} key={i}>
            <Stack sx={fieldHeader}>
              <Typography variant="body1" fontWeight={"regular"}>
                Logic Jump {i + 1}
              </Typography>

              <IconButton
                color="error"
                onClick={() =>
                  dispatch(
                    handleAddRemoveLogicJumps({
                      actionType: "remove",
                      actionIndex: i,
                    })
                  )
                }
              >
                <Remove />
              </IconButton>
            </Stack>

            <Stack sx={fieldContent}>
              <Stack gap={1}>
                <Typography
                  variant="body1"
                  fontWeight={"regular"}
                  sx={requiredStyles}
                >
                  If the field is
                </Typography>

                <Stack direction={"row"} alignItems={"center"} gap={2}>
                  <Select
                    readOnly={isPreview}
                    sx={{ width: "50%" }}
                    id={"operator"}
                    placeholder="Select"
                    inputProps={{ "aria-label": "Without label" }}
                    value={jump?.operator || ""}
                    onChange={(e) => onChange("operator", i, e)}
                  >
                    {conditionOptionMap[qType]?.map((c) => (
                      <MenuItem key={c.value} value={c.value}>
                        {c.label}
                      </MenuItem>
                    ))}
                  </Select>

                  {["text", "calculated_field_text"].includes(qType) && (
                    <TextField
                      sx={{ width: "50%" }}
                      id={"textValue"}
                      placeholder="Value"
                      value={jump.textValue ?? ""}
                      onChange={(e) => onChange("textValue", i, e)}
                      name={"textValue"}
                      InputProps={{
                        readOnly: isPreview,
                      }}
                    />
                  )}

                  {["number", "slider", "calculated_field"].includes(qType) && (
                    <TextField
                      type="number"
                      sx={{ width: "50%" }}
                      id={"numberValue"}
                      placeholder="Value"
                      value={
                        typeof jump.numberValue === "number"
                          ? jump.numberValue
                          : ""
                      }
                      onChange={(e) => onChange("numberValue", i, e, true)}
                      name={"numberValue"}
                      InputProps={{
                        readOnly: isPreview,
                      }}
                    />
                  )}

                  {["radio", "dropdown", "checkbox"].includes(qType) && (
                    <Select
                      readOnly={isPreview}
                      sx={{ width: "50%" }}
                      id={"questionChoiceId"}
                      placeholder="Select"
                      inputProps={{ "aria-label": "Without label" }}
                      value={jump.questionChoiceId || ""}
                      onChange={(e) => onChange("questionChoiceId", i, e)}
                    >
                      {choices.map((c, index) => (
                        <MenuItem key={c.ref} value={c.ref}>
                          {c.label || "Choice " + (index + 1)}
                        </MenuItem>
                      ))}
                    </Select>
                  )}

                  {["date"].includes(qType) && (
                    <LogicJumpDatePickers
                      question={question}
                      curr={jump}
                      i={i}
                    />
                  )}
                </Stack>
              </Stack>

              <Stack gap={1}>
                <Typography
                  variant="body1"
                  fontWeight={"regular"}
                  sx={requiredStyles}
                >
                  Jump to Question
                </Typography>

                <Select
                  readOnly={isPreview}
                  fullWidth
                  id={"nextQuestionId"}
                  placeholder="Select next question"
                  inputProps={{ "aria-label": "Without label" }}
                  value={jump.nextQuestionId || ""}
                  onChange={(e) => onChange("nextQuestionId", i, e)}
                >
                  {availableQuestions.length ? (
                    availableQuestions.map((q: any) => (
                      <MenuItem key={q.id} value={q.id}>
                        {q.varname} -{" "}
                        {q.label.length > 50
                          ? q.label.slice(0, 50) + "..."
                          : q.label}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem key={"None"} value={""} disabled>
                      No Questions available
                    </MenuItem>
                  )}
                </Select>
              </Stack>
            </Stack>
          </Stack>
        ))}

      <Button
        variant="outlined"
        onClick={() =>
          dispatch(
            handleAddRemoveLogicJumps({
              actionType: "add",
            })
          )
        }
        disabled={isPreview}
        sx={{ width: "fit-content" }}
      >
        Add Logic Jump
      </Button>
    </Stack>
  );
};

export default LogicJumpsTab;
